import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

import {
  useAddLeftCasesMutation,
  useLazySearchUsersQuery,
} from "../../app/admin/api";

interface AddLeftCasesModalProps {
  open: boolean;
  onClose: () => void;
}

const AddLeftCasesModal: React.FC<AddLeftCasesModalProps> = ({
  open,
  onClose,
}) => {
  const [addLeftCases, { isLoading }] = useAddLeftCasesMutation();
  const { t } = useTranslation("app");
  const [triggerSearch, { isFetching }] = useLazySearchUsersQuery();

  const [formData, setFormData] = useState({
    userId: "",
    amount: "",
  });

  const [userOptions, setUserOptions] = useState<
    { id: string | number; label: string }[]
  >([]);

  const handleSearch = async (q: string) => {
    try {
      const result = await triggerSearch({ query: q, limit: 10 }).unwrap();
      setUserOptions(result);
    } catch {
      setUserOptions([]);
    }
  };

  const handleInputChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleClose = () => {
    setFormData({
      userId: "",
      amount: "",
    });
    onClose();
  };

  const handleSubmit = async () => {
    try {
      // Validate form data
      if (!formData.userId || !formData.amount) {
        toast.error(
          t(
            "admin.addLeftCasesModal.validation.fillAll",
            "Please fill in all fields"
          )
        );
        return;
      }

      const userId = parseInt(formData.userId);
      const amount = parseInt(formData.amount);

      if (isNaN(userId) || isNaN(amount)) {
        toast.error(
          t(
            "admin.addLeftCasesModal.validation.invalidNumber",
            "Please enter valid numbers"
          )
        );
        return;
      }

      if (userId <= 0 || amount < 1) {
        toast.error(
          t(
            "admin.addLeftCasesModal.validation.amountGtZero",
            "User ID must be greater than 0 and amount must be at least 1"
          )
        );
        return;
      }

      await addLeftCases({
        id: userId,
        amount,
      }).unwrap();

      toast.success(
        t("admin.addLeftCasesModal.success", "Left cases added successfully!")
      );
      handleClose();
    } catch (error) {
      console.error("Error adding left cases:", error);
      toast.error(
        t("admin.addLeftCasesModal.error", "Failed to add left cases")
      );
    }
  };

  return (
    <Dialog maxWidth="sm" open={open} fullWidth onClose={handleClose}>
      <DialogTitle>
        <Typography component="div" variant="h6">
          {t("admin.addLeftCasesModal.title", "Add Left Cases")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Autocomplete
            filterOptions={(x) => x}
            getOptionLabel={(
              option: { id: string | number; label: string } | string
            ) => (typeof option === "string" ? option : option.label)}
            loading={isFetching}
            options={userOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                helperText={t(
                  "admin.addLeftCasesModal.userIdHelp",
                  "Enter the user ID for whom to add left cases"
                )}
                label={t("admin.addLeftCasesModal.userIdLabel", "User ID")}
              />
            )}
            onChange={(_, option) => {
              setFormData((prev) => ({
                ...prev,
                userId:
                  option && typeof option !== "string"
                    ? String((option as { id: string | number }).id)
                    : "",
              }));
            }}
            onInputChange={(_, value) => {
              if (value && value.length >= 2) {
                handleSearch(value);
              } else {
                setUserOptions([]);
              }
            }}
          />
          <TextField
            helperText={t(
              "admin.addLeftCasesModal.amountHelp",
              "Number of left cases to add (must be at least 1)"
            )}
            label={t("admin.addLeftCasesModal.amountLabel", "Amount")}
            type="number"
            value={formData.amount}
            fullWidth
            required
            onChange={handleInputChange("amount")}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button disabled={isLoading} onClick={handleClose}>
          {t("common.cancel", "Cancel")}
        </Button>
        <Button disabled={isLoading} variant="contained" onClick={handleSubmit}>
          {isLoading
            ? t("admin.addLeftCasesModal.adding", "Adding...")
            : t("admin.addLeftCasesModal.add", "Add Cases")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddLeftCasesModal;
